import type { MarketingInfo } from './application-types.ts'

const PROMO_CODE_STORAGE_KEY = 'accounts_promo_code';
const PROMO_META_STORAGE_KEY = 'accounts_promo_meta';
const PROMO_CODE_EVENT = 'promo-code-set';

export interface PromoCodeMeta {
  source?: string;
  landing_page?: string;
  utm_campaign?: string | null;
  utm_medium?: string | null;
  utm_source?: string | null;
  utm_officer?: string | null;
  utm_referrer?: string | null;
  captured_at?: string;
  [key: string]: unknown;
}

const safeJsonParse = <T>(value: string | null): T | null => {
  if (!value) {return null;}
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

export const setPromoCode = (code: string, meta?: PromoCodeMeta) => {
  if (typeof window === 'undefined') {return;}
  try {
    window.sessionStorage?.setItem(PROMO_CODE_STORAGE_KEY, code);
    if (meta) {
      window.sessionStorage?.setItem(PROMO_META_STORAGE_KEY, JSON.stringify(meta));
    }
    window.dispatchEvent(
      new CustomEvent(PROMO_CODE_EVENT, {
        detail: { promoCode: code, meta },
      })
    );
  } catch {
    // ignore storage errors
  }
};

export const getPromoCode = (): string => {
  if (typeof window === 'undefined') {return '';}
  try {
    return window.sessionStorage?.getItem(PROMO_CODE_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

export const getPromoCodeMeta = (): PromoCodeMeta | null => {
  if (typeof window === 'undefined') {return null;}
  try {
    const raw = window.sessionStorage?.getItem(PROMO_META_STORAGE_KEY) ?? null;
    return safeJsonParse<PromoCodeMeta>(raw);
  } catch {
    return null;
  }
};

export const clearPromoCode = () => {
  if (typeof window === 'undefined') {return;}
  try {
    window.sessionStorage?.removeItem(PROMO_CODE_STORAGE_KEY);
    window.sessionStorage?.removeItem(PROMO_META_STORAGE_KEY);
    window.dispatchEvent(
      new CustomEvent(PROMO_CODE_EVENT, {
        detail: { promoCode: '', meta: null },
      })
    );
  } catch {
    // ignore
  }
};

export const mergePromoCodeMeta = (partial: PromoCodeMeta) => {
  if (typeof window === 'undefined') {return;}
  const existing = getPromoCodeMeta() || {};
  const meta: PromoCodeMeta = { ...existing };
  for (const [key, value] of Object.entries(partial)) {
    if (value !== null && value !== undefined) {
      meta[key] = value;
    }
  }
  setPromoCode(getPromoCode(), meta);
};

export const restoreMarketingSession = (marketing: MarketingInfo) => {
  if (typeof window === 'undefined') {return;}
  const existing = getPromoCodeMeta() || {};
  const meta: PromoCodeMeta = {
    ...existing,
    utm_campaign: marketing.campaign || existing.utm_campaign || null,
    utm_medium: marketing.medium || existing.utm_medium || null,
    utm_source: marketing.source || existing.utm_source || null,
    utm_referrer: marketing.referrer || existing.utm_referrer || null,
    utm_officer: marketing.officer || existing.utm_officer || null,
  };
  setPromoCode(marketing.promoCode || getPromoCode(), meta);
};

export const PROMO_CODE_EVENT_NAME = PROMO_CODE_EVENT;
export const PROMO_CODE_KEY = PROMO_CODE_STORAGE_KEY;
export const PROMO_CODE_META_KEY = PROMO_META_STORAGE_KEY;



